<template>
  <div class="image-policies h-100">
    <div class="table-wrapper">
      <div class="table-header">
        <div class="table-header__actions">
          <b-button
            variant="bg-primary"
            @click="$router.push({ name: 'image-policy-create', params: {projectId: projectId} })"
          >
            <GjIcon
              class="icon-left"
              name="Plus"
            />
            Create image policy
          </b-button>
          <b-button
            variant="bg-white"
            lass="d-none d-lg-inline"
            :disabled="isEditable"
            @click="onEditClick"
          >
            Edit
          </b-button>
          <button-loading
            :spinning="removingPolicy"
            variant="bg-white"
            class="d-none d-lg-inline"
            :disabled="isEditable"
            @click="onDeleteClick"
          >
            Delete
          </button-loading>
        </div>
        <div class="default-table d-none d-lg-block">
          <div class="position-relative">
            <b-table
              ref="table"
              :items="imagePolicies"
              :fields="fields"
              :class="{ loaded: !loading }"
              select-mode="single"
              show-empty
              selectable
              responsive
              @row-selected="onRowSelected"
            >
              <template #cell(selected)="{ rowSelected }">
                <select-button :selected="rowSelected" />
              </template>

              <template #cell(name)="data">
                <a
                  class="clickable"
                  @click="onNameClick(data.item.id)"
                >{{
                  data.value
                }}</a>
              </template>

              <template #cell(lastModified)="data">
                {{ formatOffsetDateTime(data.value) || '-' }}
              </template>
              <template #empty>
                <div
                  v-if="loading"
                  class="loading-table text-center py-3"
                >
                  <b-spinner variant="primary" />
                </div>
                <template v-else>
                  <no-data
                    no-data-text="No image policy have been added yet..."
                    create-text="+ Create Image Policy"
                    access-page="Image Policies"
                    :has-permissions="hasPermissions"
                    @action="$router.push({ name: 'image-policy-create', params: {projectId: projectId} })"
                  />
                </template>
              </template>
            </b-table>
          </div>

          <div
            v-if="!loading && imagePolicies.length > 0"
            class="default-pagination"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalCount"
              :per-page="pageSize"
              first-number
              last-number
            />
            <div class="results">
              Viewing {{ first.toLocaleString() }} to {{ last.toLocaleString() }} of {{ totalCount.toLocaleString() }} Items
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- MOBILE -->
    <div class="position-relative d-lg-none mt-2">
      <div :class="{ 'opacity-7': loading }">
        <template v-for="policy in imagePolicies">
          <card
            :key="policy.id"
            :fields="fields"
            :item="policy"
            class="top-fade-in"
          >
            <template #options>
              <b-dropdown
                v-if="!policy.isGjirafaPolicy"
                id="dropdown-1"
                right
                variant="white"
                @show="select(policy)"
              >
                <template #button-content>
                  <GjIcon name="Moreoptions" />
                </template>
                <b-dropdown-item
                  @click="onEditClick"
                >
                  Edit
                </b-dropdown-item>
                <b-dropdown-item
                  @click="onDeleteClick"
                >Delete</b-dropdown-item>
              </b-dropdown>
            </template>
            <template #name>
              <a
                class="link"
                @click="onNameClick(policy.id)"
              >{{
                policy.name
              }}</a>
            </template>
            <template #lastModified>
              {{ formatOffsetDateTime(policy.lastModified) }}
            </template>
          </card>
        </template>
      </div>
      <div
        v-if="loading"
        class="mobile-loading"
      >
        <table-busy />
      </div>
      <template v-if="!loading && imagePolicies.length < 1">
        <paper-simple class="top-fade-in">
          <no-data
            class="text-center"
            no-data-text="No image policy have been added yet..."
            create-text="+ Create Image Policy"
            access-page="Image Policies"
            :has-permissions="hasPermissions"
            @action="$router.push({ name: 'image-policy-create' })"
          />
        </paper-simple>
      </template>
      <div
        v-if="!loading && imagePolicies.length > 0"
        class="default-pagination"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="pageSize"
          first-number
          last-number
        />
        <div class="results">
          Viewing {{ first.toLocaleString() }} to {{ last.toLocaleString() }} of {{ totalCount.toLocaleString() }} Items
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Card from '@/components/card/Card.vue'

export default {
  name: 'ImagePolicy',
  components: {
    Card,
  },
  data() {
    return {
      openInfo: false,
      selected: {},
      fields: [
        {
          key: 'selected',
          label: '',
          variant: 'select',
          hide: true,
        },
        {
          key: 'name',
          label: 'name',
          variant: 'fill',
          isPrimary: true,
        },
        { key: 'comment', isPrimary: true },
        'lastModified',
      ],
      hasPermissions: true,
      policy: null,
      currentPage: 1,
    }
  },
  computed: {
    imagePolicies() {
      return this.$store.state.imagePolicies.imagePolicies
    },
    imagePoliciesPagination() {
      return this.$store.state.imagePolicies.imagePoliciesPagination
    },
    first() {
      return this.imagePoliciesPagination.pageIndex * this.imagePoliciesPagination.pageSize + 1
    },
    last() {
      return this.currentPage * this.imagePoliciesPagination.pageSize < this.imagePoliciesPagination.totalCount
        ? this.currentPage * this.imagePoliciesPagination.pageSize
        : this.imagePoliciesPagination.totalCount
    },
    totalCount() {
      return this.imagePoliciesPagination.totalCount
    },
    pageSize() {
      return this.imagePoliciesPagination.pageSize || 10
    },
    pageIndex() {
      return this.currentPage - 1
    },
    projectId() {
      return this.$route.params.projectId
    },
    loading() {
      return this.$store.state.imagePolicies.fetchingPolicies
    },
    isSelected() {
      return this.selected[0]
    },
    isEditable() {
      if (this.selected[0]) {
        return this.selected[0].isGjirafaPolicy
      }
      return true
    },
    removingPolicy() {
      return this.$store.state.imagePolicies.removingPolicy
    },
  },
  watch: {
    currentPage() {
      this.onFetchImagePolicies()
    },
  },
  created() {
    this.onFetchImagePolicies()
  },
  methods: {
    select(policy) {
      this.selected = policy !== null ? [policy] : []
    },
    onRowSelected(item) {
      this.selected = item
    },
    onEditClick() {
      if (this.isSelected) {
        this.$router.push({
          name: 'image-policy-edit',
          params: { imageId: this.selected[0].id },
        })
      }
    },
    onDeleteClick() {
      this.confirmDelete(
        'Delete Image Policy',
        'Are you sure you want to delete this image policy?',
      ).then(ok => {
        if (ok) {
          this.$store
            .dispatch('imagePolicies/removeImagePolicy', this.selected[0].id)
            .then(() => {
              this.successToast('Image policy was removed')
              this.onFetchImagePolicies()
            })
            .catch(error => {
              this.errorToast(
                error.response?.data?.errors[0] || 'Something went wrong.',
              )
            })
        }
      })
    },
    onNameClick(imageId) {
      this.policy = imageId
      this.$router.push({
        name: 'image-policy-info',
        params: { imageId },
        query: { v: 'info' },
      })
    },
    onFetchImagePolicies() {
      const pagination = {
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
      }
      this.$store.dispatch('imagePolicies/fetchImagePolicies', pagination).catch(error => {
        if (error.response?.status === 403) {
          this.hasPermissions = false
          return
        }
        this.errorToast(
        error.response?.data?.errors[0]
          || 'Something went wrong while fetching image policies.',
        )
      })
    },
  },
}
</script>
